/**
 * THE PERSONA-WEIGHT REPAIR ACTION, AND THE GUARD ON THE BUTTON NEXT TO IT (task-htf7zulfpv).
 *
 * ⛔⛔ THE GAP THIS CLOSES. admin/src called .../refresh from five places and
 * research-persona-weights from ZERO. So the only surface the operator has offered the action that
 * CREATES the fault, and none for the one that fixes it — the repair was reachable only by minting
 * a JWT and POSTing by hand. Jaume is the operator; he needs a button.
 *
 * ⛔ WHY THIS IS A MODULE, not JSX pasted into one page. Personas render on TWO surfaces, and
 * PersonaHealth.tsx's header records what happened last time one of them was improved alone:
 * /admin/category-research is the DISCOVERABLE page (it is in the sidebar), while
 * /research/category-perceptions has no nav entry at all. Shipping the repair on the perceptions
 * page only would put it where nobody clicks, and leave the default click path showing a broken
 * category with no way to act on it. Both pages import this.
 *
 * ⚠️ URLS ARE RELATIVE ON PURPOSE. authFetch prefixes API_URL for any non-http url
 * (utils/auth-fetch.ts:19-32). The two host pages disagree — the perceptions page passes relative
 * paths, the admin page interpolates API_URL itself — and both happen to resolve. A shared module
 * must pick the form that is correct under both, which is the relative one.
 */
import { useState, useEffect } from 'react';
import { Wrench, Loader2, AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { authFetch } from '@/utils/auth-fetch';
import { PersonaLike, benefitWeightCount, weightlessCountOf } from './PersonaHealth';

/* ------------------------------------------------------------------------------------------- *
 *  THE REFRESH GUARD (scope item 3)
 * ------------------------------------------------------------------------------------------- */

/**
 * How many personas would LOSE curated benefit weights if this category were re-researched now.
 *
 * ⛔ THE CONDITION IS "WEIGHTS EXIST TO LOSE", NOT "SOMETHING IS BROKEN". A category whose personas
 * are all weightless has nothing to destroy, so warning there would train the operator to click
 * through the warning — and the one time it mattered they would click through that too.
 */
export const weightedCountOf = (personas: PersonaLike[]): number =>
    personas.filter((p) => benefitWeightCount(p) > 0).length;

/**
 * ⛔⛔ WHETHER TO CONFIRM — AND IT MUST NOT DEPEND ON A RELOAD HAVING SUCCEEDED.
 *
 * The defect this closes (codex leg 1 on #4753): after a successful repair the admin dialog kept
 * rendering the `category` object captured when it opened. The guards derive their predicate from
 * that object, so they still saw ZERO weighted personas — and let "Re-Research All" run WITHOUT
 * CONFIRMATION, destroying the weights the operator had just written. The dialog reported "Written"
 * on the same screen.
 *
 * ⇒ Refreshing the selected category fixes the data, and that is done too. But a guard whose
 * correctness rests on a refetch landing is a guard that silently disarms whenever the refetch
 * fails, races, or returns a stale read from a replica. ⛔ THE FAILURE IS INVISIBLE — a disarmed
 * guard looks exactly like a category that never had weights.
 * ⇒ So the guard ALSO latches on what this session KNOWS it wrote. Once a repair has been applied
 * here, refreshing this category is a destructive act regardless of what the persona list says.
 *
 * ⚠️ The latch can only over-confirm (one extra dialog on a category that turned out to have
 * nothing to lose), which is recoverable. The reload-only version fails the other way.
 */
export const shouldConfirmRefresh = (
    personas: PersonaLike[],
    weightsJustWritten: boolean,
): boolean => weightsJustWritten || weightedCountOf(personas) > 0;

/**
 * ⚠️ TRUE OF THE BACKEND AS DEPLOYED TODAY. A refresh rebuilds personas wholly from fresh research
 * and the curated weights are replaced by whatever the model returns this pass — silently, because
 * the new weights are themselves valid.
 *
 * ⛔⛔ THIS COPY HAS A KNOWN EXPIRY AND MUST BE UPDATED, NOT LEFT TO AGE. task-grlwy5lp3q (#4748)
 * changes refresh to either CARRY THE WEIGHTS ACROSS or REFUSE AND SAY WHY. Once that is deployed,
 * "this will destroy them" is false, and a stale warning on an operator-facing surface is its own
 * defect — it is the kind of wrong statement that never announces itself, because nothing breaks.
 * ⇒ WHEN #4748 IS LIVE: change this to say refresh will preserve the weights or refuse, and drop
 * the type-to-confirm gate to a plain confirm. The `PersonaRefreshConfirmDialog` test pins this
 * string so the change cannot be made silently in one place and forgotten in the other.
 */
export const REFRESH_DESTROYS_WEIGHTS = true;

export function PersonaRefreshConfirmDialog({
    open, onOpenChange, sectorId, categoryPath, personas, onConfirm, loading = false,
    scope = 'personas', weightsJustWritten = false,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    sectorId: number | undefined;
    categoryPath: string;
    personas: PersonaLike[];
    onConfirm: () => void;
    loading?: boolean;
    /**
     * ⛔⛔ 'all' IS NOT A COSMETIC VARIANT — IT EXISTS BECAUSE THE GUARD SHIPPED COVERING THE WRONG
     * BUTTON. The first version guarded only the per-aspect persona refresh (the round arrow) and
     * left "Re-Research All" — the BLUE button, the one the CS guide names first as the destroyer
     * and the one an operator actually reaches for — going straight through. A guard on the
     * obscure control and not the obvious one is worse than no guard, because the guide now
     * promises a confirmation that would not have appeared. Same predicate, same copy discipline,
     * different sentence about what is being rebuilt.
     */
    scope?: 'personas' | 'all';
    /**
     * ⛔ THE LATCH THAT ARMED THE GUARD MUST ALSO REACH THE TITLE, OR THE WARNING GOES LIMP IN
     * EXACTLY THE CASE THE LATCH EXISTS FOR (codex leg-2 minor on #4753).
     * `shouldConfirmRefresh` arms on `weightsJustWritten || weightedCountOf(personas) > 0`. So
     * after a successful repair whose reload has not landed — a stale or empty visible list — the
     * dialog OPENS on the latch while `weighted` reads 0, and the title said
     * "This will destroy 0 curated benefit-weight sets".
     * ⇒ THE HARD GATE STILL HELD, BUT AN OPERATOR READING "destroy 0" TYPES THE CONFIRM WORD AND
     * CLICKS THROUGH — the sentence talked them past the guard that had just caught the danger.
     * A warning that understates in its own edge case is worse than a blunt one.
     */
    weightsJustWritten?: boolean;
}) {
    const [typed, setTyped] = useState('');
    /**
     * ⛔⛔ RESET THE TYPED CONFIRMATION ON EVERY OPEN/CLOSE TRANSITION, NOT IN onOpenChange.
     * This dialog STAYS MOUNTED, so `typed` outlives a close. Clearing it only inside the Dialog's
     * own onOpenChange missed every real exit path: Cancel calls the PARENT onOpenChange directly,
     * and each confirm handler sets the parent's open-state itself before running the refresh.
     * ⇒ SO AFTER ONE CONFIRMED REFRESH, REOPENING THE GUARD FOUND "REFRESH" STILL TYPED AND THE
     * DESTRUCTIVE BUTTON ALREADY ARMED — the type-to-confirm gate silently spent, and the second
     * rebuild one click away. A guard that only stops you the first time is not a guard.
     * Keying on `open` covers every path in and out, however the dialog was dismissed.
     * (codex leg-2 BLOCKING at 7e17d306f7.)
     */
    useEffect(() => { setTyped(''); }, [open]);
    const weighted = weightedCountOf(personas);
    const confirmWord = 'REFRESH';
    const armed = typed.trim().toUpperCase() === confirmWord;
    /**
     * The count is not trustworthy here: the guard is open because THIS SESSION wrote weights,
     * while the personas prop still shows the pre-repair (or not-yet-reloaded) set. Say what we
     * actually know instead of a number we know is wrong.
     */
    const countIsStale = weighted === 0 && !!weightsJustWritten;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) setTyped(''); onOpenChange(o); }}>
            <DialogContent className="sm:max-w-[560px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                        </div>
                        <DialogTitle className="text-red-600">
                            {countIsStale
                                ? 'This will destroy the benefit weights repaired in this session'
                                : `This will destroy ${weighted} curated benefit-weight set${weighted === 1 ? '' : 's'}`}
                        </DialogTitle>
                    </div>
                    <DialogDescription className="pt-2">
                        {scope === 'all'
                            ? 'Re-Research All rebuilds EVERY researched aspect of this category — price tiers, brand tiers, use cases, and the buyer personas along with them. '
                            : 'Re-researching personas rebuilds them from scratch. '}
                        The benefit weights on this category were produced by a separate repair pass, and
                        this replaces them with whatever this pass returns. The result will look perfectly
                        valid, so nothing afterwards will tell you the weights changed.
                    </DialogDescription>
                </DialogHeader>

                {/* ⛔ THE ROW BEING CHANGED, VERBATIM. These rows are keyed by (sector, path) and the
                    same-looking path exists under more than one sector — the plain Wetsuits path is in
                    both 82 and 71. Naming the sector and the exact stored path is the only way the
                    operator can tell whose data they are about to overwrite. Not truncated, not
                    prettified: the string shown is the key that will be written. */}
                <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                    <div className="flex gap-2">
                        <span className="text-muted-foreground w-16 shrink-0">Sector</span>
                        <span className="font-mono">{sectorId ?? '(unknown — refusing to guess)'}</span>
                    </div>
                    <div className="flex gap-2">
                        <span className="text-muted-foreground w-16 shrink-0">Path</span>
                        <span className="font-mono break-all">{categoryPath}</span>
                    </div>
                    <div className="flex gap-2">
                        <span className="text-muted-foreground w-16 shrink-0">Personas</span>
                        <span className="font-mono">
                            {weighted} with weights, {weightlessCountOf(personas)} without
                        </span>
                    </div>
                </div>

                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <label htmlFor="persona-refresh-confirm" className="text-sm font-medium text-red-800">
                        Type <strong className="font-mono">{confirmWord}</strong> to continue
                    </label>
                    <input
                        id="persona-refresh-confirm"
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        placeholder={confirmWord}
                        autoComplete="off"
                        disabled={loading}
                        className="mt-2 w-full rounded-md border border-red-300 bg-background px-3 py-2 text-sm focus:border-red-500 focus:outline-none"
                    />
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => { if (armed && !loading) onConfirm(); }}
                        disabled={!armed || loading}
                    >
                        {loading ? 'Refreshing...' : (scope === 'all' ? 'Re-Research All anyway' : 'Refresh anyway')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ------------------------------------------------------------------------------------------- *
 *  THE REPAIR (scope item 2)
 * ------------------------------------------------------------------------------------------- */

/** The dry-run/apply envelope, as routes/category-characteristics.js:558 actually returns it. */
export interface RepairResponse {
    success?: boolean;
    dryRun?: boolean;
    error?: string;
    result?: {
        ok?: boolean;
        dry_run?: boolean;
        weightsByIndex?: Record<string, Record<string, number>>;
        evidence?: { personasCount?: number; benefitsCount?: number };
        canonicalization_failures?: Array<{ persona?: string; reason?: string; error?: string }>;
        canonicalization_unmapped?: Array<{ persona?: string | null; reason?: string; detail?: string }>;
        canonicalization?: { attempted?: number; mapped?: number; unmapped?: number; failed?: number };
        applied?: { applied?: boolean; personas_count?: number };
    };
}

/**
 * ⛔⛔ SUCCESS IS READ FROM THE FIELDS THAT ACTUALLY CARRY IT, AND NOTHING IS INFERRED FROM ABSENCE.
 * An HTTP 200 with an unexpected body must render as "unexpected", never as a green tick — a
 * repair that reports success it did not perform is worse than one that fails, because the operator
 * stops looking. Every branch below that cannot confirm the shape falls through to the raw response.
 */
/** The operator's words for each unmapped reason the server can send. Unknown reasons render as given. */
export const UNMAPPED_REASON_LABEL: Record<string, string> = {
    no_synonym_map_entry: 'No synonym-map entry exists for',
    no_name: 'No name to look up (no lookup was made) for',
};

export const groupUnmappedByReason = (
    list: Array<{ persona?: string | null; reason?: string }>,
): Record<string, string[]> => {
    const out: Record<string, string[]> = {};
    for (const u of list) {
        const reason = typeof u?.reason === 'string' && u.reason !== '' ? u.reason : 'reason not given';
        (out[reason] ??= []).push(u?.persona ?? '(unnamed)');
    }
    return out;
};

export const previewIsUsable = (r: RepairResponse): boolean =>
    r.success === true && r.result?.ok === true && r.result?.dry_run === true
    && !!r.result?.weightsByIndex && typeof r.result.weightsByIndex === 'object';

export const applyConfirmed = (r: RepairResponse): boolean =>
    r.success === true && r.result?.ok === true && r.result?.dry_run === false
    && r.result?.applied?.applied === true;

export function PersonaRepairButton({
    sectorId, categoryPath, personas, onApplied, size = 'sm', variant = 'outline', disabled = false,
}: {
    sectorId: number | undefined;
    categoryPath: string;
    personas: PersonaLike[];
    /** Called after a CONFIRMED apply, so the host can reload and show the new state. */
    onApplied: () => void | Promise<void>;
    size?: 'sm' | 'default';
    variant?: 'outline' | 'default' | 'secondary';
    /**
     * ⛔ Lets a HOST that is running a multi-step write lock this control out. The repair writes
     * benefit_weights, so racing it against a regenerate chain produces weights belonging to a
     * different persona set than the one stored. Optional and defaulting false, so every existing
     * caller is unaffected.
     */
    disabled?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState<null | 'preview' | 'apply'>(null);
    const [preview, setPreview] = useState<RepairResponse | null>(null);
    const [applied, setApplied] = useState<RepairResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    const weightless = weightlessCountOf(personas);

    /**
     * ⛔ THE REPAIR CANNOT HELP A CATEGORY WITH NO PERSONAS, AND SAYING SO IS THE WHOLE POINT.
     * PersonaBenefitWeightResearchService.loadEvidence throws
     * "category_characteristics.personas is empty" — a 500 that reads like the repair is broken
     * when in fact there is nothing to weight. Offering a button that can only fail here would
     * manufacture that misreading, so the button is not offered and the reason is stated.
     */
    /**
     * ⛔⛔ REFUSE TO WRITE WITHOUT A SECTOR. The write is keyed on (sector, category_path) and this
     * button interpolates the sector straight into the URL — an undefined one POSTs to
     * /category-characteristics/undefined/..., and on a multi-sector site a WRONG one is a paid
     * write against another sector's row.
     * ⇒ The admin page resolves the category's sector ASYNCHRONOUSLY, so there is a real window
     * where it is not yet known. Refusing here makes that window safe for BOTH surfaces at once,
     * rather than each caller having to remember. (codex both legs, blocking.)
     */
    if (sectorId == null) {
        return (
            <span className="text-xs text-muted-foreground">
                Working out which sector this category belongs to...
            </span>
        );
    }

    if (personas.length === 0) {
        return (
            <span className="text-xs text-muted-foreground">
                Research personas first — there is nothing to weight yet.
            </span>
        );
    }

    const post = async (dryRun: boolean): Promise<RepairResponse> => {
        const res = await authFetch(
            `/category-characteristics/${sectorId}/category/${encodeURIComponent(categoryPath)}/research-persona-weights`,
            {
                method: 'POST',
                // ⚠️ dryRun is sent EXPLICITLY in both directions. The route defaults it to true
                // (`req.body?.dryRun !== false`), but relying on a default for the difference
                // between a preview and a paid write means a change to that default silently
                // becomes a write from this button.
                body: JSON.stringify({ dryRun }),
            }
        );
        return (await res.json()) as RepairResponse;
    };

    const runPreview = async () => {
        setOpen(true);
        setBusy('preview');
        setPreview(null);
        setApplied(null);
        setError(null);
        try {
            const data = await post(true);
            setPreview(data);
            if (!previewIsUsable(data)) {
                setError(data.error || 'The preview returned a shape this screen does not recognise.');
            }
        } catch (e: any) {
            setError(e?.message || String(e));
        } finally {
            setBusy(null);
        }
    };

    const runApply = async () => {
        setBusy('apply');
        setError(null);
        try {
            const data = await post(false);
            setApplied(data);
            if (applyConfirmed(data)) {
                await onApplied();
            } else {
                setError(data.error || 'The write returned a shape this screen does not recognise — treat it as NOT applied and check the row.');
            }
        } catch (e: any) {
            setError(e?.message || String(e));
        } finally {
            setBusy(null);
        }
    };

    const failures = preview?.result?.canonicalization_failures ?? [];
    /**
     * ⛔ THE BACKEND WILL REFUSE THE WRITE while canonicalization failures are present, unless the
     * caller passes allowPartialCanonicalization (route :551-560). Surfacing that here rather than
     * letting the operator press Apply and receive a 500 they cannot interpret.
     * ⇒ The partial-write opt-in is deliberately NOT offered as a checkbox: it exists to make
     * accepting an incomplete write an explicit, considered act, and a tick-box next to the button
     * would turn it back into a default.
     */
    const applyBlocked = failures.length > 0;
    /**
     * ⚠️ UNMAPPED IS INFORMATION, NOT A BLOCK (task-ush8qlgr72). A persona with no synonym-map row is
     * a designed null — the server writes the weights and leaves canonical_persona_id null — but it
     * used to be reported as NOTHING beside an empty failure list, so an operator could not tell a
     * fully canonicalised category from one the canonical layer has never heard of. The server now
     * lists them with reasons; they are shown here so the count is on screen before Apply.
     */
    const unmapped = preview?.result?.canonicalization_unmapped ?? [];
    const canonSummary = preview?.result?.canonicalization;
    const weightsByIndex = preview?.result?.weightsByIndex ?? {};

    return (
        <>
            <Button size={size} variant={variant} onClick={runPreview} disabled={busy !== null || disabled}
                title="Research and write benefit weights for this category's existing personas. Shows a preview first; nothing is written until you confirm.">
                {busy === 'preview' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
                Repair benefit weights
                {weightless > 0 && (
                    <Badge variant="destructive" className="ml-2 text-[10px]">{weightless} need it</Badge>
                )}
            </Button>

            <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
                <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Repair benefit weights</DialogTitle>
                        <DialogDescription>
                            Benefit weights decide which buyer persona a product page is written for. This
                            researches them for the personas already on this category — it does not replace
                            the personas themselves.
                        </DialogDescription>
                    </DialogHeader>

                    {/* The row being changed — same contract as the refresh guard above. */}
                    <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                        <div className="flex gap-2">
                            <span className="text-muted-foreground w-16 shrink-0">Sector</span>
                            <span className="font-mono">{sectorId ?? '(unknown — refusing to guess)'}</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-muted-foreground w-16 shrink-0">Path</span>
                            <span className="font-mono break-all">{categoryPath}</span>
                        </div>
                    </div>

                    {busy === 'preview' && (
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Researching a preview — nothing is being written.
                        </p>
                    )}

                    {error && (
                        <div className="rounded-md border border-red-300 bg-red-50 p-3">
                            <p className="text-sm font-medium text-red-800 flex items-center gap-2">
                                <ShieldAlert className="w-4 h-4" /> The server said:
                            </p>
                            {/* ⛔ THE REAL MESSAGE, NOT A FRIENDLY SUBSTITUTE. The backend's refusals name
                                the sector, the path and what to do next; replacing them with "something
                                went wrong" throws away the only part that is actionable. */}
                            <pre className="mt-1 text-xs text-red-900 whitespace-pre-wrap break-words">{error}</pre>
                        </div>
                    )}

                    {preview && previewIsUsable(preview) && !applied && (
                        <div className="space-y-2">
                            <p className="text-sm font-medium">
                                Preview — nothing written yet.{' '}
                                <span className="text-muted-foreground font-normal">
                                    {preview.result?.evidence?.personasCount ?? '?'} personas against{' '}
                                    {preview.result?.evidence?.benefitsCount ?? '?'} canonical benefits.
                                </span>
                            </p>
                            <div className="rounded-md border divide-y">
                                {Object.entries(weightsByIndex).map(([idx, weights]) => {
                                    const i = Number(idx);
                                    const p = personas[i];
                                    // ⛔ AN INDEX WITH NO PERSONA IS NAMED, NOT BLANKED. weightsByIndex is
                                    // keyed by the service's persona_index; if that ever stops lining up
                                    // with the list this screen loaded, a blank row would read as a
                                    // rendering glitch instead of the mismatch it is.
                                    const label = p
                                        ? (p.persona_key || '(unnamed persona)')
                                        : `persona index ${idx} — NOT in the list this screen loaded`;
                                    const n = weights && typeof weights === 'object' ? Object.keys(weights).length : 0;
                                    return (
                                        <div key={idx} className="flex items-center justify-between gap-3 p-2 text-xs">
                                            <span className={p ? 'font-medium' : 'font-medium text-amber-700'}>{label}</span>
                                            <span className="text-muted-foreground shrink-0">
                                                {n} benefit{n === 1 ? '' : 's'} weighted
                                                {p && benefitWeightCount(p) > 0 && (
                                                    <span className="ml-1 text-amber-700">
                                                        (replaces {benefitWeightCount(p)})
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {unmapped.length > 0 && (
                                <div className="rounded-md border border-slate-300 bg-slate-50 p-3 text-xs" data-testid="canonicalization-unmapped">
                                    <p className="font-medium text-slate-900">
                                        {unmapped.length}
                                        {canonSummary && Number.isInteger(canonSummary.attempted) ? ` of ${canonSummary.attempted}` : ''}
                                        {' '}persona{unmapped.length === 1 ? '' : 's'} have no canonical persona
                                    </p>
                                    {/* codex #5835 round 1: the reason is the SERVER'S, one line per reason, never a single
                                        hard-coded diagnosis — a `no_name` persona has nothing to map and no remedy in the map. */}
                                    {Object.entries(groupUnmappedByReason(unmapped)).map(([reason, names]) => (
                                        <p key={reason} className="mt-1 text-slate-700" data-reason={reason}>
                                            {UNMAPPED_REASON_LABEL[reason] ?? `Reason "${reason}"`}:{' '}
                                            <span className="font-mono">{names.join(', ')}</span>
                                        </p>
                                    ))}
                                    <p className="mt-1 text-slate-700">
                                        Weights will still be written; canonical_persona_id stays null for these until they are mapped.
                                    </p>
                                </div>
                            )}

                            {applyBlocked && (
                                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs">
                                    <p className="font-medium text-amber-900 flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" /> Applying will be refused
                                    </p>
                                    <p className="mt-1 text-amber-900">
                                        {failures.length} persona{failures.length === 1 ? '' : 's'} could not be
                                        matched to a canonical persona, and the server will not persist weights
                                        without that metadata:{' '}
                                        <span className="font-mono">
                                            {failures.map((f) => f?.persona ?? '(unnamed)').join(', ')}
                                        </span>
                                        . Resolve those first, then run this again.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* An unrecognised preview body is shown raw rather than summarised away. */}
                    {preview && !previewIsUsable(preview) && (
                        <pre className="rounded-md border bg-muted/40 p-3 text-[11px] overflow-x-auto whitespace-pre-wrap break-words">
                            {JSON.stringify(preview, null, 2)}
                        </pre>
                    )}

                    {applied && applyConfirmed(applied) && (
                        <div className="rounded-md border border-green-300 bg-green-50 p-3">
                            <p className="text-sm font-medium text-green-800 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4" /> Written.
                            </p>
                            <p className="text-xs text-green-900 mt-1">
                                {applied.result?.applied?.personas_count ?? '?'} personas now carry benefit weights
                                on sector {sectorId} / {categoryPath}.
                            </p>
                        </div>
                    )}
                    {applied && !applyConfirmed(applied) && (
                        <pre className="rounded-md border bg-muted/40 p-3 text-[11px] overflow-x-auto whitespace-pre-wrap break-words">
                            {JSON.stringify(applied, null, 2)}
                        </pre>
                    )}

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={busy !== null}>
                            {applied && applyConfirmed(applied) ? 'Close' : 'Cancel'}
                        </Button>
                        {/* ⛔ THE SECOND EXPLICIT ACT. Preview and apply are never collapsed into one
                            click: this is a paid LLM write to a row a human may have curated. */}
                        {preview && previewIsUsable(preview) && !applied && (
                            <Button onClick={runApply} disabled={busy !== null || applyBlocked}>
                                {busy === 'apply' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                Apply these weights
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
